/* eslint-disable max-lines */
import type { ApolloCache, Reference, StoreObject } from '@apollo/client';
import { gql } from '@apollo/client';

import type {
  GraphQLAuditItem,
  GraphQLAuditItemWithDecisions,
  DeleteAuditDecisionResponse,
  SkipAuditItemResponse,
  UnskipAuditItemResponse,
  SelectAuditDecisionResponse,
  GraphQLAuditDecision,
} from '../domain/graphql-types';

/**
 * Centralized Apollo Cache Update Utilities for Audit Operations
 *
 * This module provides standardized cache update patterns for all audit mutations
 * to ensure consistent UI reactivity and eliminate mixed cache update approaches.
 *
 * Key Principles:
 * - Use surgical cache.modify() for specific field updates
 * - Invalidate dependent queries for reactive updates
 * - Maintain referential equality for unchanged data
 * - Use complete data from mutation responses
 */

/**
 * Updates an audit item in the Apollo cache with surgical precision
 *
 * @param cache - Apollo cache instance
 * @param auditItem - Complete audit item data from mutation response
 */
export const updateAuditItemInCache = (cache: ApolloCache<unknown>, auditItem: GraphQLAuditItem | GraphQLAuditItemWithDecisions) => {
  // Surgical cache.modify() for the specific item
  cache.modify({
    id: cache.identify({ __typename: 'AuditItem', id: auditItem.id }),
    fields: {
      // Update core item fields
      status: () => auditItem.status,
      lastModifiedAt: () => auditItem.lastModifiedAt,

      // Update decision-related fields if available
      ...('decisions' in auditItem && {
        decisions: () => auditItem.decisions,
        decisionsCount: () => auditItem.decisionsCount,
        selectedDecisionId: () => auditItem.selectedDecisionId,
      }),
    },
  });

  // Also update the AuditItemWithDecisions variant if it exists in cache
  cache.modify({
    id: cache.identify({ __typename: 'AuditItemWithDecisions', id: auditItem.id }),
    fields: {
      status: () => auditItem.status,
      lastModifiedAt: () => auditItem.lastModifiedAt,

      ...('decisions' in auditItem && {
        decisions: () => auditItem.decisions,
        decisionsCount: () => auditItem.decisionsCount,
        selectedDecisionId: () => auditItem.selectedDecisionId,
      }),
    },
  });
};

/**
 * Invalidates session stats cache to trigger reactive progress updates
 *
 * @param cache - Apollo cache instance
 * @param auditSessionId - ID of the audit session
 */
export const invalidateSessionStats = (cache: ApolloCache<unknown>, auditSessionId: string) => {
  // Invalidate session stats for reactive progress bar updates
  cache.evict({
    fieldName: 'auditSessionStats',
    args: { auditSessionId },
  });
};

/**
 * Complete cache update for audit item mutations
 * Combines item update and stats invalidation
 *
 * @param cache - Apollo cache instance
 * @param auditItem - Complete audit item data from mutation response
 */
export const updateAuditItemAndStats = (cache: ApolloCache<unknown>, auditItem: GraphQLAuditItem | GraphQLAuditItemWithDecisions) => {
  updateAuditItemInCache(cache, auditItem);
  invalidateSessionStats(cache, auditItem.auditSessionId);
};

/**
 * Invalidates decisions cache for session-level reactivity
 * Use when decisions are added/removed/selected
 *
 * @param cache - Apollo cache instance
 * @param auditSessionId - ID of the audit session
 */
export const invalidateSessionDecisions = (cache: ApolloCache<unknown>, auditSessionId: string) => {
  // Evict all auditDecisions queries regardless of their variables
  cache.evict({
    fieldName: 'auditDecisions',
    broadcast: true, // Ensure all components are notified
  });

  // Also try the specific args pattern that matches the query variables
  cache.evict({
    fieldName: 'auditDecisions',
    args: { auditSessionId }, // Match the query variable name
    broadcast: true,
  });

  // Try the old pattern in case some queries use it
  cache.evict({
    fieldName: 'auditDecisions',
    args: { sessionId: auditSessionId },
    broadcast: true,
  });

  // Force garbage collection to clean up orphaned references
  cache.gc();
};

/**
 * Invalidates audit items cache for pagination reactivity
 * Use when items are updated and need to reflect in paginated results
 *
 * @param cache - Apollo cache instance
 * @param auditSessionId - ID of the audit session
 */
export const invalidateSessionItems = (cache: ApolloCache<unknown>, auditSessionId: string) => {
  // Evict all auditItems queries regardless of their variables (page, pageSize, filters)
  // This will invalidate all cached queries for this field
  cache.evict({
    fieldName: 'auditItems',
    broadcast: true, // Ensure all components are notified
  });

  // Also try the specific args pattern in case some queries use it
  cache.evict({
    fieldName: 'auditItems',
    args: { input: { auditSessionId } },
    broadcast: true,
  });

  // Force garbage collection to clean up orphaned references
  cache.gc();
};

/**
 * Complete cache update for decision mutations
 * Updates item, invalidates stats, decisions, and paginated items
 *
 * @param cache - Apollo cache instance
 * @param auditItem - Complete audit item data from mutation response
 */
export const updateAuditDecisionAndCaches = (cache: ApolloCache<unknown>, auditItem: GraphQLAuditItemWithDecisions) => {
  // Update the specific audit item in cache (contains the new decision)
  updateAuditItemInCache(cache, auditItem);

  // Only invalidate stats (which change when decisions are added)
  invalidateSessionStats(cache, auditItem.auditSessionId);

  // Write the new decision directly to the normalized cache
  // This makes it immediately available to all components without refetching
  if (auditItem.decisions && auditItem.decisions.length > 0) {
    // Get the latest decision (the one we just created/updated)
    const latestDecision = auditItem.decisions[auditItem.decisions.length - 1];

    // Write the decision directly to the cache using cache.modify
    cache.modify({
      fields: {
        auditDecisions(existingDecisions = { decisions: [] }, { readField }) {
          // Check if this decision already exists in the cache
          const existingDecisionIndex = existingDecisions.decisions.findIndex(
            (decision: Reference | StoreObject) => readField('id', decision) === latestDecision.id
          );

          if (existingDecisionIndex >= 0) {
            // Update existing decision
            const updatedDecisions = [...existingDecisions.decisions];

            updatedDecisions[existingDecisionIndex] = latestDecision;

            return { ...existingDecisions, decisions: updatedDecisions };
          } else {
            // Add new decision
            return {
              ...existingDecisions,
              decisions: [...existingDecisions.decisions, latestDecision],
            };
          }
        },
      },
    });
  }
};

/**
 * Evicts a specific audit decision from cache
 * Used for decision deletion operations
 *
 * @param cache - Apollo cache instance
 * @param decisionId - ID of the decision to evict
 */
export const evictAuditDecision = (cache: ApolloCache<unknown>, decisionId: string) => {
  cache.evict({
    id: cache.identify({ __typename: 'AuditDecision', id: decisionId }),
  });

  cache.evict({
    id: cache.identify({ __typename: 'AuditDecisionResponse', id: decisionId }),
  });
};

/**
 * Removes a specific decision from the auditDecisions cache optimistically
 * Used for true optimistic delete operations without triggering refetches
 *
 * @param cache - Apollo cache instance
 * @param decisionId - ID of the decision to remove
 * @param auditSessionId - ID of the audit session
 */
export const removeDecisionFromCache = (cache: ApolloCache<unknown>, decisionId: string) => {
  // Remove the decision from the auditDecisions cache directly
  cache.modify({
    fields: {
      auditDecisions(existingDecisions = { decisions: [] }, { readField }) {
        // Filter out the deleted decision
        const updatedDecisions = existingDecisions.decisions.filter((decision: Reference | StoreObject) => readField('id', decision) !== decisionId);

        return { ...existingDecisions, decisions: updatedDecisions };
      },
    },
  });

  // Evict the specific decision from cache
  evictAuditDecision(cache, decisionId);
};

/**
 * Complete optimistic delete operation for audit decisions
 * Removes decision from cache and updates stats without triggering refetches
 *
 * @param cache - Apollo cache instance
 * @param decisionId - ID of the decision to delete
 * @param auditSessionId - ID of the audit session
 */
export const optimisticDeleteAuditDecision = (cache: ApolloCache<unknown>, decisionId: string, auditSessionId: string) => {
  // Remove decision from auditDecisions cache immediately
  removeDecisionFromCache(cache, decisionId);

  // Update stats cache directly instead of invalidating
  // Note: We'll still invalidate stats since we don't have the exact new counts
  // but this is much faster than invalidating decisions too
  invalidateSessionStats(cache, auditSessionId);
};

/**
 * Creates an optimistic response for audit decision deletion
 * This simulates what the server response would look like on successful deletion
 *
 * @param cache - Apollo cache instance
 * @param auditItemId - ID of the audit item
 * @param decisionId - ID of the decision to delete
 * @returns Optimistic response matching DeleteAuditDecisionResponse structure
 */
export const createOptimisticDeleteResponse = (cache: ApolloCache<unknown>, auditItemId: string, decisionId: string): DeleteAuditDecisionResponse | null => {
  try {
    // Get current audit item from cache
    const auditItemCacheId = cache.identify({ __typename: 'AuditItem', id: auditItemId });
    const currentAuditItem = cache.readFragment<Partial<GraphQLAuditItem>>({
      id: auditItemCacheId,
      fragment: gql`
        fragment OptimisticDeleteAuditItem on AuditItemWithDecisions {
          id
          type
          createdBy
          createdAt
          lastModifiedBy
          lastModifiedAt
          auditSessionId
          status
          position
          contentSnapshot {
            audioFileUrl
            transcription
            language
            topic
            contentType
            classificationResults {
              name
              severity
            }
            metadata
            triggeredPolicies {
              id
              name
              communityId
              description
              isEnabled
              labels {
                labelId
                severity
              }
              createdAt
              createdBy
              lastModifiedAt
              lastModifiedBy
            }
            nonTriggeredPolicies {
              id
              name
              communityId
              description
              isEnabled
              labels {
                labelId
                severity
              }
              createdAt
              createdBy
              lastModifiedAt
              lastModifiedBy
            }
            slmModelVersion
            slmPromptVersion
            quicktxtModelVersion
          }
          kustoReference {
            communityId
            subjectId
            timestamp
          }
          decisions {
            id
            auditItemId
            auditSessionId
            auditorId
            auditorName
            timestamp
            updatedAt
            isSelected
            selectedBy
            selectedAt
            languageCorrect
            transcriptionCorrect
            correctedTranscription
            originalLabels {
              name
              severity
            }
            correctedLabels {
              name
              severity
            }
            comments
            isCurrentUser
          }
          decisionsCount
          selectedDecisionId
        }
      `,
    });

    if (!currentAuditItem) {
      return null;
    }

    // Create optimistic response - simulate successful deletion
    return {
      deleteAuditDecision: {
        auditItem: {
          ...(currentAuditItem as GraphQLAuditItem),
          decisions: currentAuditItem.decisions?.filter((d: { id: string }) => d.id !== decisionId) || [],
          decisionsCount: Math.max(0, (currentAuditItem.decisionsCount || 1) - 1),
          lastModifiedAt: new Date().toISOString(),
        },
      },
    };
  } catch (error) {
    console.warn('Failed to create optimistic delete response:', error);

    return null;
  }
};

/**
 * Batch update multiple audit items efficiently
 * Used for bulk operations like "Agree All"
 *
 * @param cache - Apollo cache instance
 * @param auditItems - Array of audit items to update
 * @param auditSessionId - ID of the audit session
 */
export const batchUpdateAuditItems = (
  cache: ApolloCache<unknown>,
  auditItems: (GraphQLAuditItem | GraphQLAuditItemWithDecisions)[],
  auditSessionId: string
) => {
  // Update all items individually
  auditItems.forEach(item => updateAuditItemInCache(cache, item));

  // Single invalidation for the entire batch
  invalidateSessionStats(cache, auditSessionId);
  invalidateSessionDecisions(cache, auditSessionId);
  invalidateSessionItems(cache, auditSessionId);
};

/**
 * Creates an optimistic response for audit item skip operation
 * This simulates what the server response would look like on successful skip
 *
 * @param cache - Apollo cache instance
 * @param auditItemId - ID of the audit item to skip
 * @returns Optimistic response matching SkipAuditItemResponse structure
 */
export const createOptimisticSkipResponse = (cache: ApolloCache<unknown>, auditItemId: string): SkipAuditItemResponse | null => {
  try {
    // Get current audit item from cache
    const auditItemCacheId = cache.identify({ __typename: 'AuditItem', id: auditItemId });
    const currentAuditItem = cache.readFragment<Partial<GraphQLAuditItem>>({
      id: auditItemCacheId,
      fragment: gql`
        fragment OptimisticSkipAuditItem on AuditItem {
          id
          type
          createdBy
          createdAt
          lastModifiedBy
          lastModifiedAt
          auditSessionId
          status
          position
          decisionsCount
          selectedDecisionId
          kustoReference {
            communityId
            subjectId
            timestamp
          }
          contentSnapshot {
            audioFileUrl
            transcription
            language
            topic
            contentType
            classificationResults {
              name
              severity
            }
            metadata
            triggeredPolicies {
              id
              name
              communityId
              description
              isEnabled
              labels {
                labelId
                severity
              }
              createdAt
              createdBy
              lastModifiedAt
              lastModifiedBy
            }
            nonTriggeredPolicies {
              id
              name
              communityId
              description
              isEnabled
              labels {
                labelId
                severity
              }
              createdAt
              createdBy
              lastModifiedAt
              lastModifiedBy
            }
            slmModelVersion
            slmPromptVersion
            quicktxtModelVersion
          }
        }
      `,
    });

    if (!currentAuditItem) {
      return null;
    }

    // Create optimistic response - simulate successful skip
    return {
      skipAuditItem: {
        item: {
          ...(currentAuditItem as GraphQLAuditItem),
          status: 'skipped',
          lastModifiedAt: new Date().toISOString(),
        },
      },
    };
  } catch (error) {
    console.warn('Failed to create optimistic skip response:', error);

    return null;
  }
};

/**
 * Creates an optimistic response for audit item unskip operation
 * This simulates what the server response would look like on successful unskip
 *
 * @param cache - Apollo cache instance
 * @param auditItemId - ID of the audit item to unskip
 * @returns Optimistic response matching UnskipAuditItemResponse structure
 */
export const createOptimisticUnskipResponse = (cache: ApolloCache<unknown>, auditItemId: string): UnskipAuditItemResponse | null => {
  try {
    // Get current audit item from cache
    const auditItemCacheId = cache.identify({ __typename: 'AuditItem', id: auditItemId });
    const currentAuditItem = cache.readFragment<Partial<GraphQLAuditItem>>({
      id: auditItemCacheId,
      fragment: gql`
        fragment OptimisticUnskipAuditItem on AuditItem {
          id
          type
          createdBy
          createdAt
          lastModifiedBy
          lastModifiedAt
          auditSessionId
          status
          position
          decisionsCount
          selectedDecisionId
          kustoReference {
            communityId
            subjectId
            timestamp
          }
          contentSnapshot {
            audioFileUrl
            transcription
            language
            topic
            contentType
            classificationResults {
              name
              severity
            }
            metadata
            triggeredPolicies {
              id
              name
              communityId
              description
              isEnabled
              labels {
                labelId
                severity
              }
              createdAt
              createdBy
              lastModifiedAt
              lastModifiedBy
            }
            nonTriggeredPolicies {
              id
              name
              communityId
              description
              isEnabled
              labels {
                labelId
                severity
              }
              createdAt
              createdBy
              lastModifiedAt
              lastModifiedBy
            }
            slmModelVersion
            slmPromptVersion
            quicktxtModelVersion
          }
        }
      `,
    });

    if (!currentAuditItem) {
      return null;
    }

    // Create optimistic response - simulate successful unskip (return to pending status)
    return {
      unskipAuditItem: {
        item: {
          ...(currentAuditItem as GraphQLAuditItem),
          status: 'pending',
          lastModifiedAt: new Date().toISOString(),
        },
      },
    };
  } catch (error) {
    console.warn('Failed to create optimistic unskip response:', error);

    return null;
  }
};

/**
 * Creates an optimistic response for audit decision selection
 * This simulates what the server response would look like on successful selection
 *
 * @param cache - Apollo cache instance
 * @param auditItemId - ID of the audit item
 * @param decisionId - ID of the decision to select
 * @returns Optimistic response matching SelectAuditDecisionResponse structure
 */
export const createOptimisticSelectResponse = (cache: ApolloCache<unknown>, auditItemId: string, decisionId: string): SelectAuditDecisionResponse | null => {
  try {
    // Get current audit item from cache
    const auditItemCacheId = cache.identify({ __typename: 'AuditItem', id: auditItemId });
    const currentAuditItem = cache.readFragment<Partial<GraphQLAuditItem>>({
      id: auditItemCacheId,
      fragment: gql`
        fragment OptimisticSelectAuditItem on AuditItemWithDecisions {
          id
          type
          createdBy
          createdAt
          lastModifiedBy
          lastModifiedAt
          auditSessionId
          status
          position
          contentSnapshot {
            audioFileUrl
            transcription
            language
            topic
            contentType
            classificationResults {
              name
              severity
            }
            metadata
            triggeredPolicies {
              id
              name
              communityId
              description
              isEnabled
              labels {
                labelId
                severity
              }
              createdAt
              createdBy
              lastModifiedAt
              lastModifiedBy
            }
            nonTriggeredPolicies {
              id
              name
              communityId
              description
              isEnabled
              labels {
                labelId
                severity
              }
              createdAt
              createdBy
              lastModifiedAt
              lastModifiedBy
            }
            slmModelVersion
            slmPromptVersion
            quicktxtModelVersion
          }
          kustoReference {
            communityId
            subjectId
            timestamp
          }
          decisions {
            id
            auditItemId
            auditSessionId
            auditorId
            auditorName
            timestamp
            updatedAt
            isSelected
            selectedBy
            selectedAt
            languageCorrect
            transcriptionCorrect
            correctedTranscription
            originalLabels {
              name
              severity
            }
            correctedLabels {
              name
              severity
            }
            comments
            isCurrentUser
          }
          decisionsCount
          selectedDecisionId
        }
      `,
    });

    if (!currentAuditItem || !currentAuditItem.decisions) {
      return null;
    }

    // Create optimistic response - simulate successful selection
    const updatedDecisions = currentAuditItem.decisions.map((decision: GraphQLAuditDecision) => ({
      ...decision,
      isSelected: decision.id === decisionId,
      selectedBy: decision.id === decisionId ? 'current-user' : decision.selectedBy,
      selectedAt: decision.id === decisionId ? new Date().toISOString() : decision.selectedAt,
    }));

    return {
      selectAuditDecision: {
        auditItem: {
          ...(currentAuditItem as GraphQLAuditItem),
          decisions: updatedDecisions,
          selectedDecisionId: decisionId,
          lastModifiedAt: new Date().toISOString(),
        },
      },
    };
  } catch (error) {
    console.warn('Failed to create optimistic select response:', error);

    return null;
  }
};

/**
 * Updates decisions in the auditDecisions cache to reflect selection changes
 * Used for optimistic updates when selecting decisions
 *
 * @param cache - Apollo cache instance
 * @param auditItem - Complete audit item data with updated decisions
 */
export const updateDecisionsInCache = (cache: ApolloCache<unknown>, auditItem: GraphQLAuditItemWithDecisions) => {
  // Update the auditDecisions cache with the new decision states
  cache.modify({
    fields: {
      auditDecisions(existingDecisions = { decisions: [] }, { readField }) {
        if (!auditItem.decisions) return existingDecisions;

        // Update each decision in the cache with the new selection state
        const updatedDecisions = existingDecisions.decisions.map((cachedDecision: Reference | StoreObject) => {
          const cachedDecisionId = readField('id', cachedDecision);
          const updatedDecision = auditItem.decisions.find((d: GraphQLAuditDecision) => d.id === cachedDecisionId);

          if (updatedDecision) {
            // Return the updated decision from the mutation response
            return updatedDecision;
          }

          // Return the existing decision if not found in updates
          return cachedDecision;
        });

        return { ...existingDecisions, decisions: updatedDecisions };
      },
    },
  });
};

/**
 * Immediately updates decision selection state in cache for optimistic UI feedback
 * Used before mutation executes to provide instant visual feedback
 *
 * @param cache - Apollo cache instance
 * @param auditItemId - ID of the audit item
 * @param decisionId - ID of the decision to select
 */
export const updateSelectionStateInCache = (cache: ApolloCache<unknown>, auditItemId: string, decisionId: string) => {
  // Update the audit item's selectedDecisionId immediately
  cache.modify({
    id: cache.identify({ __typename: 'AuditItem', id: auditItemId }),
    fields: {
      selectedDecisionId: () => decisionId,
      lastModifiedAt: () => new Date().toISOString(),
    },
  });

  // Also update the AuditItemWithDecisions variant if it exists
  cache.modify({
    id: cache.identify({ __typename: 'AuditItemWithDecisions', id: auditItemId }),
    fields: {
      selectedDecisionId: () => decisionId,
      lastModifiedAt: () => new Date().toISOString(),
    },
  });

  // Update the decisions in the auditDecisions cache immediately
  cache.modify({
    fields: {
      auditDecisions(existingDecisions = { decisions: [] }, { readField }) {
        // Update each decision's selection state
        const updatedDecisions = existingDecisions.decisions.map((cachedDecision: Reference | StoreObject) => {
          const cachedDecisionId = readField('id', cachedDecision);
          const cachedAuditItemId = readField('auditItemId', cachedDecision);

          // Only update decisions for this audit item
          if (cachedAuditItemId === auditItemId) {
            // Create updated decision with new selection state
            return {
              ...cachedDecision,
              isSelected: cachedDecisionId === decisionId,
              selectedBy: cachedDecisionId === decisionId ? 'current-user' : readField('selectedBy', cachedDecision),
              selectedAt: cachedDecisionId === decisionId ? new Date().toISOString() : readField('selectedAt', cachedDecision),
            };
          }

          return cachedDecision;
        });

        return { ...existingDecisions, decisions: updatedDecisions };
      },
    },
  });
};

/**
 * Rollback selection state in cache when mutation fails
 * Restores the previous selection state to undo optimistic updates
 *
 * @param cache - Apollo cache instance
 * @param auditItemId - ID of the audit item
 * @param previousSelectedDecisionId - Previous selected decision ID (or null if none)
 */
export const rollbackSelectionStateInCache = (cache: ApolloCache<unknown>, auditItemId: string, previousSelectedDecisionId: string | null) => {
  // Restore the audit item's selectedDecisionId
  cache.modify({
    id: cache.identify({ __typename: 'AuditItem', id: auditItemId }),
    fields: {
      selectedDecisionId: () => previousSelectedDecisionId,
    },
  });

  // Also restore the AuditItemWithDecisions variant if it exists
  cache.modify({
    id: cache.identify({ __typename: 'AuditItemWithDecisions', id: auditItemId }),
    fields: {
      selectedDecisionId: () => previousSelectedDecisionId,
    },
  });

  // Restore the decisions in the auditDecisions cache
  cache.modify({
    fields: {
      auditDecisions(existingDecisions = { decisions: [] }, { readField }) {
        // Restore each decision's selection state
        const restoredDecisions = existingDecisions.decisions.map((cachedDecision: Reference | StoreObject) => {
          const cachedDecisionId = readField('id', cachedDecision);
          const cachedAuditItemId = readField('auditItemId', cachedDecision);

          // Only restore decisions for this audit item
          if (cachedAuditItemId === auditItemId) {
            // Restore original selection state
            return {
              ...cachedDecision,
              isSelected: cachedDecisionId === previousSelectedDecisionId,
              selectedBy: cachedDecisionId === previousSelectedDecisionId ? readField('selectedBy', cachedDecision) : null,
              selectedAt: cachedDecisionId === previousSelectedDecisionId ? readField('selectedAt', cachedDecision) : null,
            };
          }

          return cachedDecision;
        });

        return { ...existingDecisions, decisions: restoredDecisions };
      },
    },
  });
};
